"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"

type ArticleItem = {
  id: string
  slug: string
  title: string
  subtitle: string | null
  imageUrl: string | null
  tags: string | null
  publishedAt: Date | string | null
  author: { name: string | null }
}

const PAGE_SIZE = 12

export default function ArticleGrid({ articles }: { articles: ArticleItem[] }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const visible = articles.slice(0, visibleCount)
  const hasMore = visibleCount < articles.length

  return (
    <>
      <h2 className="text-2xl font-semibold mb-6">Latest</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {visible.map((article) => {
          const publishedAt = new Date(article.publishedAt ?? Date.now())
          return (
            <Link
              key={article.id}
              href={`/magazine/${article.slug}`}
              className="group flex flex-col"
            >
              {/* Cover */}
              <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4 bg-[var(--muted)]">
                {article.imageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#e6f0ef] to-[#a4e4d8]" />
                )}
              </div>

              {/* Meta */}
              {article.tags?.split(",")[0] && (
                <p className="text-xs font-semibold text-[var(--primary)] uppercase tracking-widest mb-2 font-[family-name:var(--font-sans)]">
                  {article.tags.split(",")[0].trim()}
                </p>
              )}

              {/* Title */}
              <h3 className="font-editorial text-xl font-semibold leading-snug mb-1 group-hover:text-[var(--primary)] transition-colors">
                {article.title}
              </h3>

              {article.subtitle && (
                <p className="text-sm text-[var(--muted-foreground)] line-clamp-2 leading-relaxed flex-1 font-[family-name:var(--font-sans)]">
                  {article.subtitle}
                </p>
              )}

              <p className="text-xs text-[var(--muted-foreground)] mt-3 font-[family-name:var(--font-sans)]">
                {publishedAt.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </Link>
          )
        })}
      </div>

      {hasMore && (
        <div className="mt-12 text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            Load more articles
          </Button>
        </div>
      )}
    </>
  )
}
